import { toast } from "sonner";
import { useOnboarding } from "@/context/OnboardingContext";
import useUserInteractionHandling from "./useUserInteractionHandling";
import config from "@/config";

export default function useContentGenerationAndWorkflow() {
  const { state, setState, likedIdeas } = useOnboarding();
  const { handleUpdateUrlParams, handleHasLikedIdeas } = useUserInteractionHandling();

  // When ideas are first shown (in handleStartSetup1 after generating ideas)
  const handleIdeasGenerated = async (ideas: any[]) => {
    // Store the ideas in localStorage
    localStorage.setItem("post_ideas", JSON.stringify(ideas));
    try {
      const website_id = localStorage.getItem("website_id");
      const website_url = localStorage.getItem("onboardingWebsite");

      await fetch(`${config.supabase.functionsUrl}/store-onboarding-analytics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          website_id,
          website_url,
          status: "ideas_shown",
          // Persist the full list of ideas shown to the user immediately
          post_theme_suggestions: ideas,
        }),
      });
    } catch (error) {
      console.error("Error updating onboarding analytics:", error);
    }
  };

  // Add handleGenerateNewIdeas function
  const handleGenerateNewIdeas = async () => {
    // Implementation of handleGenerateNewIdeas
    const postIdeas = JSON.parse(localStorage.getItem("post_ideas") || "[]");
    const newIdeas = postIdeas.map((idea) => ({
      ...idea,
      hidden: false,
      liked: false,
    }));
    localStorage.setItem("post_ideas", JSON.stringify(newIdeas));
    return newIdeas;
  };

  // Generate content based on liked idea
  const handleStartContentGeneration = () => {
    // Reset progress
    setState((prev) => ({ ...prev, progress: 0 }));

    // Get first liked idea
    const postIdeas = JSON.parse(localStorage.getItem("post_ideas") || "[]");
    const likedIdea = postIdeas.find((idea: any) => likedIdeas.includes(idea.id));

    if (!likedIdea) {
      toast.error("No liked ideas found. Please select an idea first.");
      return;
    }

    console.log("Starting content generation with website URL:", state.websiteUrl);

    try {
      // Start progress animation - adjusted to take approximately 15 seconds
      // Changed to 1% every 200ms to make the loading take around 15 seconds
      const progressInterval = setInterval(() => {
        setState((prev) => {
          const newProgress = Math.min(prev.progress + 1, 95); // Increment by 1% each time

          // Update the step text based on progress to make it more animated
          let stepText = "Analyzing your tone and style...";

          if (newProgress < 20) {
            stepText = "Analyzing your tone and style...";
          } else if (newProgress < 40) {
            stepText = "Analyzing your tone and style...\n\nStructuring your content...";
          } else if (newProgress < 60) {
            stepText =
              "Analyzing your tone and style...\n\nStructuring your content...\n\nWriting engaging paragraphs...";
          } else if (newProgress < 80) {
            stepText =
              "Analyzing your tone and style...\n\nStructuring your content...\n\nWriting engaging paragraphs...\n\nPolishing your content...";
          } else {
            stepText =
              "Analyzing your tone and style...\n\nStructuring your content...\n\nWriting engaging paragraphs...\n\nPolishing your content...\n\nAlmost ready...";
          }

          return { ...prev, progress: newProgress, currentStepText: stepText };
        });
      }, 200); // Set to 200ms interval

      // Get the scraped content and key pages from localStorage
      const scrapedContent = JSON.parse(localStorage.getItem("scraped_content") || "[]");
      const keyContentPages = JSON.parse(
        localStorage.getItem("key_content_pages") || "[]"
      );

      console.log("Content generation context:", {
        scrapedContentCount: scrapedContent.length,
        keyPagesCount: keyContentPages.length,
      });

      // Format the cornerstone content for link generation
      // This matches the database format expected by generate-content-v3
      const cornerstoneContent = keyContentPages.map((page) => ({
        title: page.title || "",
        url: page.url || "",
        is_cornerstone: true,
        content: page.content || "",
        digest: page.digest || "",
      }));

      // Format the scraped content to match database format
      const formattedScrapedContent = scrapedContent.map((item) => ({
        title: item.title || "",
        url: item.url || item.original_url || "",
        content: item.content || "",
        digest: item.digest || "",
        is_cornerstone: false,
      }));

      // Call generate-content-v3 with properly formatted data
      handleCallEdgeFunction('onboarding-generate-content', {
        website_id: localStorage.getItem('website_id') || '',
        website_url: state.websiteUrl,
        title: likedIdea.title,
        language: localStorage.getItem('website_language') || 'en',
        format_template: likedIdea.format_template,
        keywords: likedIdea.keywords,
        length_tag: likedIdea.length_tag,
        cornerstone_content: cornerstoneContent,
        scraped_content: formattedScrapedContent
      })
        .then((result) => {
          // Clear the progress interval
          clearInterval(progressInterval);

          if (result?.content) {
            // Add a small delay before showing the final result to make the transition smoother
            setTimeout(() => {
              // Complete the progress bar
              setState((prev) => ({
                ...prev,
                progress: 100,
                contentGenerated: true,
                generatedContentTitle: likedIdea.title,
                generatedContentPreview: result.content,
                currentStepText:
                  "Analyzing your tone and style...\n\nStructuring your content...\n\nWriting engaging paragraphs...\n\nPolishing your content...\n\nYour content is ready!",
              }));

              // Store generated content in localStorage
              const generatedContent = {
                title: likedIdea.title,
                content: result.content,
                status: "textgenerated",
                created_at: new Date().toISOString(),
                website_id: localStorage.getItem("website_id"),
              };
              localStorage.setItem("generated_content", JSON.stringify(generatedContent));

              // Call handleContentGenerated to update status
              handleContentGenerated(generatedContent);
            }, 500); // Add a 500ms delay for a smoother transition
          } else {
            // Handle error case
            clearInterval(progressInterval);
            setState((prev) => ({ ...prev, progress: 0 }));

            toast.error("Failed to generate content. Please try again.");

            console.error("No content returned from generate-content-v3 function");
          }
        })
        .catch((error) => {
          // Clear the progress interval
          clearInterval(progressInterval);
          setState((prev) => ({ ...prev, progress: 0 }));

          toast.error("Failed to generate content. Please try again.");

          console.error("Error generating content:", error);
        });
    } catch (error) {
      setState((prev) => ({ ...prev, progress: 0 }));

      toast("Error", {
        description: error.message || "Failed to generate content. Please try again.",
      });

      console.error("Error calling generate-content-v3:", error);
    }
  };

  // Helper function to call Supabase Edge Functions directly without authentication
  const handleCallEdgeFunction = async (functionName: string, body: any) => {
    console.log("Calling edge function:", functionName, body);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(body),
          mode: "cors",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error("Edge function error response:", errorData);
        throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Failed to call ${functionName}:`, error);
      throw error;
    }
  };

  // When content is generated and shown
  const handleContentGenerated = async (content: any) => {
    try {
      const website_id = localStorage.getItem("website_id");
      const website_url = localStorage.getItem("onboardingWebsite");

      await fetch(`${config.supabase.functionsUrl}/store-onboarding-analytics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          website_id,
          website_url,
          status: "post_shown",
          // Store the generated post content when it is first shown
          post_theme_content: content,
        }),
      });
    } catch (error) {
      console.error("Error updating onboarding analytics:", error);
    }
  };

  // Move to Setup 3: Content Generation
  const handleContinueToContentCreation = async () => {
    if (!handleHasLikedIdeas()) {
      toast.error("Please like at least one content idea to continue");
      return;
    }

    // Get all liked ideas before continuing
    const postIdeas = JSON.parse(localStorage.getItem("post_ideas") || "[]");
    const likedIdeasData = postIdeas.filter((idea: any) => likedIdeas.includes(idea.id));

    // Update onboarding analytics with selected ideas and status
    try {
      const website_id = localStorage.getItem("website_id");
      const website_url = localStorage.getItem("onboardingWebsite");

      await fetch(`${config.supabase.functionsUrl}/store-onboarding-analytics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          website_id,
          website_url,
          status: "ideas_accepted",
          post_theme_suggestions: likedIdeasData, // Store the selected ideas here
        }),
      });
    } catch (error) {
      console.error("Error updating onboarding analytics:", error);
    }

    setState((prev) => ({
      ...prev,
      step: 3,
      progress: 0,
      contentGenerated: false,
    }));

    // Update URL to reflect current step
    handleUpdateUrlParams({ step: "post-draft" });

    handleStartContentGeneration();
  };

  // Regenerate content
  const handleRegenerateContent = () => {
    setState((prev) => ({
      ...prev,
      contentGenerated: false,
      progress: 0,
    }));

    handleStartContentGeneration();
  };

  return {
    handleIdeasGenerated,
    handleGenerateNewIdeas,
    handleStartContentGeneration,
    handleCallEdgeFunction,
    handleContentGenerated,
    handleContinueToContentCreation,
    handleRegenerateContent,
  };
}
